import React, { createContext, useContext, useState, useEffect } from 'react';
import { pageApi, sectionApi, cardApi } from '../services/api';

const ContentContext = createContext();

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export const ContentProvider = ({ children }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all pages
  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await pageApi.getAll();
      setPages(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get page by slug
  const fetchPageBySlug = async (slug) => {
    try {
      setLoading(true);
      const response = await pageApi.getBySlug(slug);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create a new page
  const createPage = async (pageData) => {
    try {
      const response = await pageApi.create(pageData);
      setPages([...pages, response.data]);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Update a page
  const updatePage = async (id, pageData) => {
    try {
      const response = await pageApi.update(id, pageData);
      setPages(pages.map(page => page.id === id ? response.data : page));
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Delete a page
  const deletePage = async (id) => {
    try {
      await pageApi.delete(id);
      setPages(pages.filter(page => page.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Section Management
  const createSection = async (sectionData) => {
    try {
      const response = await sectionApi.create(sectionData);
      // The API returns { section: createdSection, page: updatedPage }
      // We need to update the entire pages array with the updated page
      if (response.data.page) {
        setPages(pages.map(page => 
          page.id === response.data.page.id ? response.data.page : page
        ));
      }
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateSection = async (id, sectionData) => {
    try {
      const response = await sectionApi.update(id, sectionData);
      // The API returns { section: updatedSection, page: updatedPage }
      if (response.data.page) {
        setPages(pages.map(page => 
          page.id === response.data.page.id ? response.data.page : page
        ));
      }
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteSection = async (id, pageId) => {
    try {
      const response = await sectionApi.delete(id);
      // The API returns { message: 'Section deleted successfully', page: updatedPage }
      if (response.data.page) {
        setPages(pages.map(page => 
          page.id === pageId ? response.data.page : page
        ));
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Card Management
  const createCard = async (cardData) => {
    try {
      const response = await cardApi.create(cardData);
      // The API returns { card: createdCard, page: updatedPage }
      if (response.data.page) {
        setPages(pages.map(page => 
          page.id === response.data.page.id ? response.data.page : page
        ));
      }
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateCard = async (id, cardData) => {
    try {
      const response = await cardApi.update(id, cardData);
      // The API returns { card: updatedCard, page: updatedPage }
      if (response.data.page) {
        setPages(pages.map(page => 
          page.id === response.data.page.id ? response.data.page : page
        ));
      }
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteCard = async (id, sectionId) => {
    try {
      const response = await cardApi.delete(id);
      // The API returns { message: 'Card deleted successfully', page: updatedPage }
      if (response.data.page) {
        setPages(pages.map(page => 
          page.id === response.data.page.id ? response.data.page : page
        ));
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const value = {
    pages,
    loading,
    error,
    fetchPages,
    fetchPageBySlug,
    createPage,
    updatePage,
    deletePage,
    createSection,
    updateSection,
    deleteSection,
    createCard,
    updateCard,
    deleteCard
  };

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
}; 